/**
 * A menu's buttons, walkable with the arrows and pressable with Enter.
 *
 * Every menu in the game is a short list of buttons, and every one of them is
 * met without a mouse in hand: the front page comes up before pointer lock, and
 * the pause and finish screens are exactly where the mouse has just been handed
 * back. So the highlight lives here rather than being re-invented per screen,
 * and `Game` gives the keys to whichever list is on top - see `keyboardMenu`.
 *
 * This owns no DOM of its own on purpose. The screens lay their buttons out
 * differently (a column on the front page, a row at the finish, one button in a
 * card's foot) and none of that is this class's business; it is handed the
 * buttons and manages which one is lit.
 *
 * The highlight does not carry browser focus around with it. A focused button
 * answers Enter *itself*, on the keydown, and `dom.ts` lets it have the key -
 * the game never sees it. Tab is the one thing that focuses a button, so the
 * highlight follows focus rather than racing it, and the arrows drag focus
 * along once it is here. Both paths can then only ever press the same button.
 */

export class MenuButtons {
  private selected = 0;
  private active = false;
  private nodes: readonly HTMLButtonElement[] = [];
  private readonly onFocus = (event: FocusEvent) => {
    this.select(this.nodes.indexOf(event.currentTarget as HTMLButtonElement));
  };

  constructor(buttons: readonly HTMLButtonElement[], private readonly scrollSelection = false) {
    this.setButtons(buttons);
  }

  /** Refresh asynchronously loaded rows without accumulating focus listeners. */
  setButtons(buttons: readonly HTMLButtonElement[]): void {
    const focused = document.activeElement;
    this.nodes.forEach((node) => node.removeEventListener('focus', this.onFocus));
    this.nodes = buttons;
    this.nodes.forEach((node) => node.addEventListener('focus', this.onFocus));
    const index = this.nodes.indexOf(focused as HTMLButtonElement);
    this.select(index >= 0 ? index : Math.min(this.selected, Math.max(0, buttons.length - 1)));
  }

  /** Arrows: move the highlight, wrapping at both ends. */
  move(delta: number): void {
    const count = this.nodes.length;
    if (!count) return;
    const next = (this.selected + delta + count) % count;
    const wasFocused = this.nodes.includes(document.activeElement as HTMLButtonElement);
    this.select(next);
    // Only if focus was already in the list: an arrow key must not be what
    // takes focus off, say, the promille box on the settings page.
    if (wasFocused) this.nodes[next].focus();
  }

  /** Enter: press whatever the highlight is on. */
  activate(): void {
    this.nodes[this.selected]?.click();
  }

  /**
   * Whether these buttons are on screen. Driven from the screen's own
   * visibility every frame, like everything else `refreshScreens` decides, so
   * it has to be cheap and idempotent - and only the edge does anything.
   *
   * A menu that comes back up starts at the top of its list. Whoever returns to
   * one wants its first button far more often than the one they happened to
   * leave the highlight sitting on.
   */
  setActive(active: boolean): void {
    if (active === this.active) return;
    this.active = active;
    if (active) this.select(0);
  }

  private select(index: number): void {
    this.selected = index;
    if (this.active && this.scrollSelection) this.nodes[index]?.scrollIntoView({ block: 'nearest' });
    this.nodes.forEach((node, i) => node.classList.toggle('btn--selected', i === index));
  }
}
